/**
 * AboutPage — the public "story" page for keenez.
 *
 * Premium, restrained, JP-B2B (安心感) marketing surface that mirrors the v2
 * landing redesign: GradientMesh hero, SectionShell rhythm, Reveal/Stagger
 * scroll motion, TiltCard value cards, a CountUp proof band, and a closing CTA.
 *
 * Conventions honored (CONVENTIONS ARE LAW):
 *  - Renders inside the public chrome (SiteHeader + SiteFooter).
 *  - All building blocks come from '@/components/landing/v2'.
 *  - Existing about.* i18n keys preserved (story.p1–p3, values.*, etc).
 *  - NEW copy uses t(key, { defaultValue }) so nothing needs a locale edit.
 *  - Single indigo→fuchsia accent, ink grays, logical props (ps/pe/start/end),
 *    font-display/font-jp + gradient-text tokens, mobile-first, focus-visible.
 *  - Motion is gated by Reveal/usePrefersReducedMotion — calm, purposeful.
 */
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  ArrowLeft,
  Heart,
  Globe2,
  Users,
  Layers,
  Calculator,
  Languages,
  ShieldCheck,
  Sparkles,
  MapPin,
  type LucideIcon,
} from 'lucide-react';

import { SiteHeader } from '@/components/layout/SiteHeader';
import { SiteFooter } from '@/components/layout/SiteFooter';
import {
  Reveal,
  Stagger,
  SectionShell,
  GradientMesh,
  CountUp,
  TiltCard,
} from '@/components/landing/v2';
import { fadeInUp, fadeInLeft, fadeInRight } from '@/components/landing/v2/animations';

// ── Value cards — reuse the existing about.values.* copy, elevated visually ──
interface ValueDef {
  key: 'craft' | 'borders' | 'honesty' | 'depth';
  Icon: LucideIcon;
}
const VALUES: ValueDef[] = [
  { key: 'craft', Icon: Heart },
  { key: 'borders', Icon: Globe2 },
  { key: 'honesty', Icon: Users },
  { key: 'depth', Icon: Layers },
];

// ── Proof band — every figure is TRUE to the product ─────────────────────────
interface StatDef {
  id: string;
  Icon: LucideIcon;
  to: number;
  suffix?: string;
  labelKey: string;
  labelDefault: string;
}
const STATS: StatDef[] = [
  {
    id: 'engines',
    Icon: Calculator,
    to: 4,
    labelKey: 'about.stats.engines',
    labelDefault: 'Native payroll engines — JP · US · UK · DE',
  },
  {
    id: 'locales',
    Icon: Languages,
    to: 14,
    labelKey: 'about.stats.locales',
    labelDefault: 'Localized languages at full parity',
  },
  {
    id: 'vault',
    Icon: ShieldCheck,
    to: 256,
    labelKey: 'about.stats.vault',
    labelDefault: 'AES-256-GCM per-workspace encryption vault',
  },
  {
    id: 'sources',
    Icon: Sparkles,
    to: 1,
    suffix: '',
    labelKey: 'about.stats.source',
    labelDefault: 'One source of truth — AI-native, both-sides-deep',
  },
];

export function AboutPage() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen flex flex-col">
      <SiteHeader />

      <main className="flex-1">
        {/* ── Hero / mission ───────────────────────────────────────────── */}
        <section className="relative isolate overflow-hidden pt-20 pb-16 md:pt-28 md:pb-24">
          <GradientMesh />
          <div className="container relative z-10 mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
            <Stagger>
              <Reveal>
                <Link
                  to="/"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-md"
                >
                  <ArrowLeft aria-hidden className="h-3.5 w-3.5 rtl:rotate-180" />
                  {t('legal.backHome', { defaultValue: 'Back to home' })}
                </Link>
              </Reveal>

              <Reveal as="p" className="mt-6 text-xs uppercase tracking-[0.2em] text-primary font-semibold">
                {t('about.eyebrow', { defaultValue: 'About' })}
              </Reveal>

              <Reveal
                as="div"
                className="display serif-jp mt-3 text-4xl md:text-6xl font-semibold tracking-tight text-balance"
              >
                <h1>
                  {t('about.hero.headline', { defaultValue: 'HR, played as an ensemble.' })}
                </h1>
              </Reveal>

              <Reveal as="p" className="mt-5 text-base md:text-xl text-muted-foreground leading-relaxed text-pretty max-w-2xl">
                {t('about.hero.lede', {
                  defaultValue:
                    'One platform for your Japan team, your global team, and everyone in between. Native depth on both sides — no stitching, no part-time bilingual ops, no second system.',
                })}
              </Reveal>

              <Reveal as="p" className="mt-5 text-base md:text-lg text-muted-foreground leading-relaxed text-pretty max-w-2xl">
                {t('about.subtitle')}
              </Reveal>
            </Stagger>
          </div>
        </section>

        {/* ── Why we exist (narrative) ─────────────────────────────────── */}
        <SectionShell
          id="story"
          width="6xl"
          eyebrow={t('about.story.eyebrow', { defaultValue: 'Why we exist' })}
          heading={t('about.story.title')}
        >
          <div className="grid gap-10 lg:grid-cols-12 lg:gap-12">
            {/* Narrative column */}
            <Reveal variants={fadeInLeft} className="lg:col-span-7">
              <div className="space-y-5 text-base md:text-lg leading-relaxed text-foreground/80 text-pretty">
                <p>{t('about.story.p1')}</p>
                <p>{t('about.story.p2')}</p>
                <p>{t('about.story.p3')}</p>
              </div>
            </Reveal>

            {/* Pull-quote / the gap, visualized */}
            <Reveal variants={fadeInRight} className="lg:col-span-5">
              <div className="rounded-3xl border border-border glass-strong p-6 shadow-card-lift md:p-8">
                <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">
                  {t('about.story.gapLabel', { defaultValue: 'The hidden cross-border tax' })}
                </p>

                <div className="mt-5 flex flex-wrap items-center gap-2">
                  {['SmartHR', 'Deel', 'Spreadsheets'].map((tool) => (
                    <span
                      key={tool}
                      className="num inline-flex items-center rounded-lg border border-border bg-background/70 px-2.5 py-1 text-[12px] font-medium text-muted-foreground line-through decoration-muted-foreground/40"
                    >
                      {tool}
                    </span>
                  ))}
                </div>

                <div className="my-5 flex items-center gap-2 text-muted-foreground">
                  <span className="h-px flex-1 bg-border" />
                  <ArrowRight aria-hidden className="h-4 w-4 rtl:rotate-180" />
                  <span className="h-px flex-1 bg-border" />
                </div>

                <div className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-br from-indigo-500 to-fuchsia-500 px-4 py-2.5 text-white shadow-glow">
                  <Sparkles aria-hidden className="h-4 w-4" />
                  <span className="font-display text-lg font-semibold tracking-tight">keenez</span>
                </div>

                <p className="mt-5 text-sm leading-relaxed text-muted-foreground text-pretty">
                  {t('about.story.gapBody', {
                    defaultValue:
                      'One UI, one source of truth, one bill — built to be both-sides-deep, so nothing falls into the gap.',
                  })}
                </p>
              </div>
            </Reveal>
          </div>
        </SectionShell>

        {/* ── Values grid ──────────────────────────────────────────────── */}
        <SectionShell
          id="values"
          tone="muted"
          align="center"
          width="6xl"
          eyebrow={t('about.values.eyebrow', { defaultValue: 'What we believe' })}
          heading={t('about.values.title')}
          subhead={t('about.values.subtitle', {
            defaultValue:
              'A small set of convictions that decide every line we ship and every yen we compute.',
          })}
          background={<GradientMesh blobs={false} />}
        >
          <Stagger as="ul" className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            {VALUES.map(({ key, Icon }) => (
              <Reveal key={key} as="li" variants={fadeInUp} className="h-full">
                <TiltCard className="h-full">
                  <div className="flex h-full flex-col p-6">
                    <span
                      aria-hidden
                      className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-fuchsia-500 text-white shadow-glow"
                    >
                      <Icon className="h-5 w-5" strokeWidth={1.75} />
                    </span>
                    <h3 className="mt-4 text-lg font-semibold tracking-tight text-foreground">
                      {t(`about.values.${key}.title`)}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground text-pretty">
                      {t(`about.values.${key}.body`)}
                    </p>
                  </div>
                </TiltCard>
              </Reveal>
            ))}
          </Stagger>
        </SectionShell>

        {/* ── Proof band (CountUp) ─────────────────────────────────────── */}
        <SectionShell
          id="proof"
          align="center"
          width="6xl"
          eyebrow={t('about.stats.eyebrow', { defaultValue: 'Built deep on both sides' })}
          heading={t('about.stats.title', { defaultValue: 'The product, in numbers.' })}
        >
          <Stagger as="ul" className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {STATS.map(({ id, Icon, to, suffix, labelKey, labelDefault }) => (
              <Reveal
                key={id}
                as="li"
                variants={fadeInUp}
                className="flex h-full flex-col items-center rounded-2xl border border-border bg-card/80 p-6 text-center shadow-soft backdrop-blur-sm"
              >
                <span
                  aria-hidden
                  className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-accent-50 text-accent-600 ring-1 ring-accent-100 dark:bg-accent-500/10 dark:text-accent-300 dark:ring-accent-500/20"
                >
                  <Icon className="h-5 w-5" strokeWidth={1.75} />
                </span>
                <CountUp
                  to={to}
                  suffix={suffix}
                  as="p"
                  className="mt-4 text-4xl md:text-5xl font-bold tracking-tight gradient-text"
                />
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground text-pretty">
                  {t(labelKey, { defaultValue: labelDefault })}
                </p>
              </Reveal>
            ))}
          </Stagger>

          <Reveal
            as="p"
            variants={fadeInUp}
            delay={0.1}
            className="mt-8 flex items-center justify-center gap-2 text-center text-xs text-muted-foreground"
          >
            <MapPin aria-hidden className="h-3.5 w-3.5 text-muted-foreground" />
            {t('about.stats.footnote', {
              defaultValue:
                'Small, distributed across Tokyo, Berlin, and San Francisco — and obsessed with HR.',
            })}
          </Reveal>
        </SectionShell>

        {/* ── Closing CTA ──────────────────────────────────────────────── */}
        <section className="relative isolate overflow-hidden py-20 md:py-28">
          <GradientMesh grid={false} />
          <div className="container relative z-10 mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
            <Reveal>
              <h2 className="display serif-jp text-3xl md:text-5xl font-semibold tracking-tight text-balance">
                {t('about.cta.title', { defaultValue: 'See keenez run your payroll.' })}
              </h2>
            </Reveal>
            <Reveal as="p" delay={0.05} className="mx-auto mt-4 max-w-2xl text-base md:text-lg text-muted-foreground leading-relaxed text-pretty">
              {t('about.cta.body', {
                defaultValue:
                  'Start free, or talk to the team that built it. Either way, you keep one source of truth.',
              })}
            </Reveal>
            <Reveal delay={0.1} className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link
                to="/signup"
                className="gradient-primary inline-flex h-12 items-center gap-2 rounded-xl px-7 text-sm font-semibold text-white shadow-glow transition hover:shadow-glow-pink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                {t('about.cta.primary', { defaultValue: 'Start for free' })}
                <ArrowRight aria-hidden className="h-4 w-4 rtl:rotate-180" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex h-12 items-center gap-2 rounded-xl border border-border bg-card/70 px-7 text-sm font-semibold text-foreground shadow-soft transition hover:shadow-card-lift focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                {t('about.cta.secondary', { defaultValue: 'Talk to us' })}
              </Link>
            </Reveal>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
